import React from 'react';
import { Heart, Music, MapPin, Users } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';

export const CultureSection = () => {
  const culturePoints = [
    {
      icon: MapPin,
      title: 'Orlando & Daytona Roots',
      description: 'Founded in Central Florida with a commitment to quality, wellness, and community connection.'
    },
    {
      icon: Music,
      title: 'Culture & Creativity',
      description: 'Where wellness meets lifestyle. Inspired by music, art, and authentic self-expression.'
    },
    {
      icon: Heart,
      title: 'Building Together',
      description: 'Supporting local communities through wellness, opportunity, and positive energy.'
    },
    {
      icon: Users,
      title: 'Join the Movement',
      description: 'More than products—a community focused on health, growth, and good vibes.'
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-black to-zinc-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-amber-400 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-lime-400 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge className="bg-lime-400/10 text-lime-400 border-lime-400/20 mb-4">
            Culture & Community
          </Badge>
          <h2 className="text-4xl sm:text-5xl font-black text-white mb-4">
            More Than a <span className="text-amber-400">Brand</span>
          </h2>
          <p className="text-xl text-gray-300">
            We're a movement. A lifestyle. A community of creators, hustlers, and dreamers
            who refuse to settle for ordinary.
          </p>
        </div>

        {/* Culture Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {culturePoints.map((point, index) => {
            const Icon = point.icon;
            return (
              <Card
                key={index}
                className="bg-zinc-900 border-zinc-800 hover:border-amber-400/50 transition-all group text-center"
              >
                <CardContent className="pt-8 pb-8">
                  <div className="bg-amber-400/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-amber-400/20 transition-colors">
                    <Icon className="w-8 h-8 text-amber-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2 group-hover:text-amber-400 transition-colors">
                    {point.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {point.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Image Gallery */}
        <div className="grid md:grid-cols-3 gap-4">
          <div className="relative h-64 rounded-xl overflow-hidden group">
            <img
              src="https://customer-assets.emergentagent.com/job_drinkstack/artifacts/eb9ajmd9_Screenshot_20250802-195733%20%281%29.png"
              alt="TEEAZE Culture"
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-end p-6">
              <p className="text-white font-bold">Streetwear Meets Wellness</p>
            </div>
          </div>
          
          <div className="relative h-64 rounded-xl overflow-hidden group">
            <img
              src="https://customer-assets.emergentagent.com/job_drinkstack/artifacts/omc8j0ri_Screenshot_2025-11-18-11-22-46-446.jpg"
              alt="Fresh Juices"
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-end p-6">
              <p className="text-white font-bold">Real Juice, Real Energy</p>
            </div>
          </div>
          
          <div className="relative h-64 rounded-xl overflow-hidden group">
            <img
              src="https://customer-assets.emergentagent.com/job_drinkstack/artifacts/zyki6bzk_Screenshot_2025-11-18-12-51-45-502.jpg"
              alt="Community"
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-end p-6">
              <p className="text-white font-bold">Built for Legends</p>
            </div>
          </div>
        </div>

        {/* Brand Mission */}
        <div className="mt-12 text-center">
          <Card className="bg-gradient-to-br from-amber-400/5 to-zinc-900 border-amber-400/20 max-w-2xl mx-auto">
            <CardContent className="pt-8 pb-8">
              <Heart className="w-12 h-12 text-amber-400 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">
                Our Mission
              </h3>
              <p className="text-gray-300 mb-4">
                Bringing together quality nutrition, conscious fashion, and community spirit.
                Every bottle, every tee, every connection matters.
              </p>
              <p className="text-sm text-gray-400">
                Building a healthier, more connected tomorrow—one sip, one step at a time.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
